import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useAuthStore } from '../lib/authStore';
import {
  useCreatePhotoCategory,
  useDeletePhoto,
  useDeletePhotoCategory,
  usePhotoCategories,
  useProjectPhotos,
  useUpdatePhoto,
  type Photo,
  type PhotoCategory,
} from '../lib/photos';
import { apiErrorMessage } from '../lib/api';
import { PhotoUploadButton } from './PhotoUploadButton';
import { AuthedImage } from './AuthedImage';
import { PhotoLightbox } from './PhotoLightbox';
import { ConfirmDialog } from './ConfirmDialog';

interface PhotosPanelProps {
  projectId: number;
}

const DELETE_GRACE_MS = 24 * 3600 * 1000;

/**
 * Foto-Bereich im Projekt-Detail. Kategorien-Tabs links, Galerie
 * rechts. Upload + Kategorie-Anlage für alle Rollen. Löschen für
 * PM/Admin oder Uploader binnen 24 h.
 */
export function PhotosPanel({ projectId }: PhotosPanelProps) {
  const { user } = useAuthStore();
  const isPmOrAdmin = user?.role === 'admin' || user?.role === 'project_manager';

  const categoriesQ = usePhotoCategories(projectId);
  const [activeCategoryId, setActiveCategoryId] = useState<number | null | 'all'>('all');
  const photosQ = useProjectPhotos(
    projectId,
    activeCategoryId === 'all' ? undefined : activeCategoryId,
  );

  const createCategory = useCreatePhotoCategory(projectId);
  const deleteCategoryM = useDeletePhotoCategory(projectId);
  const deletePhotoM = useDeletePhoto(projectId);
  const updatePhotoM = useUpdatePhoto(projectId);

  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Photo | null>(null);
  const [confirmDeleteCategory, setConfirmDeleteCategory] = useState<PhotoCategory | null>(null);

  const photos = photosQ.data ?? [];

  const canDeletePhoto = (p: Photo): boolean => {
    if (!user) return false;
    if (isPmOrAdmin) return true;
    if (p.uploaded_by !== user.id) return false;
    const age = Date.now() - new Date(p.uploaded_at + 'Z').getTime();
    return age < DELETE_GRACE_MS;
  };

  const handleCreateCategory = () => {
    const name = window.prompt(
      'Name der neuen Kategorie:\n(z.B. "Einlagen Decke UG", "Verteiler EG")',
    );
    if (!name) return;
    createCategory.mutate(name, {
      onError: (err) => alert(apiErrorMessage(err, 'Kategorie konnte nicht angelegt werden')),
    });
  };

  const currentUploadCategoryId = activeCategoryId === 'all' ? null : activeCategoryId;

  // Prev/Next im Lightbox
  const lightboxPhoto = lightboxIdx != null ? photos[lightboxIdx] : null;
  const hasPrev = lightboxIdx != null && lightboxIdx > 0;
  const hasNext = lightboxIdx != null && lightboxIdx < photos.length - 1;

  const groupedByCategory = useMemo(() => {
    // Bei "all" gruppieren wir die Fotos nach Kategorie für eine
    // sinnvolle Übersicht. Bei spezifischer Kategorie: flache Liste.
    if (activeCategoryId !== 'all') return null;
    const groups = new Map<string, Photo[]>();
    for (const p of photos) {
      const key = p.category_name ?? '— ohne Kategorie';
      const arr = groups.get(key) ?? [];
      arr.push(p);
      groups.set(key, arr);
    }
    return Array.from(groups.entries());
  }, [photos, activeCategoryId]);

  return (
    <div className="card" style={{ marginTop: 20, padding: 0 }}>
      <div
        style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--color-border-soft)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 10,
        }}
      >
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-navy)', margin: 0 }}>
          Fotos
          <span style={{ color: 'var(--color-text-soft)', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
            ({photos.length})
          </span>
        </h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <PhotoUploadButton
            projectId={projectId}
            presetCategoryId={currentUploadCategoryId}
            label="Fotos hochladen"
          />
          <PhotoUploadButton
            projectId={projectId}
            presetCategoryId={currentUploadCategoryId}
            label="📷 Kamera"
            cameraOnly
          />
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={handleCreateCategory}
          >
            + Kategorie
          </button>
        </div>
      </div>

      {/* Kategorie-Tabs */}
      <div
        style={{
          display: 'flex',
          gap: 4,
          padding: '8px 20px',
          borderBottom: '1px solid var(--color-border-soft)',
          overflowX: 'auto',
          flexWrap: 'wrap',
        }}
      >
        <CategoryTab
          active={activeCategoryId === 'all'}
          onClick={() => setActiveCategoryId('all')}
        >
          Alle
        </CategoryTab>
        {(categoriesQ.data ?? []).map((c) => (
          <CategoryTab
            key={c.id}
            active={activeCategoryId === c.id}
            onClick={() => setActiveCategoryId(c.id)}
            onDelete={
              isPmOrAdmin && c.photo_count === 0
                ? () => setConfirmDeleteCategory(c)
                : undefined
            }
          >
            {c.name} <span style={{ color: 'var(--color-text-soft)' }}>({c.photo_count})</span>
          </CategoryTab>
        ))}
        <CategoryTab
          active={activeCategoryId === null}
          onClick={() => setActiveCategoryId(null)}
        >
          Ohne Kategorie
        </CategoryTab>
      </div>

      {/* Galerie */}
      <div style={{ padding: 20 }}>
        {photosQ.isLoading ? (
          <div className="loading">Lade Fotos …</div>
        ) : photosQ.error ? (
          <div className="empty-state">
            <p>{apiErrorMessage(photosQ.error)}</p>
          </div>
        ) : photos.length === 0 ? (
          <div className="empty-state" style={{ padding: 30 }}>
            <p>Noch keine Fotos in {activeCategoryId === 'all' ? 'diesem Projekt' : 'dieser Kategorie'}.</p>
          </div>
        ) : groupedByCategory ? (
          // Alle-Sicht: nach Kategorie gruppiert
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {groupedByCategory.map(([catName, items]) => (
              <div key={catName}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--color-text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    marginBottom: 10,
                  }}
                >
                  {catName} <span style={{ fontWeight: 400 }}>({items.length})</span>
                </div>
                <Grid
                  photos={items}
                  allPhotos={photos}
                  onOpen={(idx) => setLightboxIdx(idx)}
                />
              </div>
            ))}
          </div>
        ) : (
          <Grid photos={photos} allPhotos={photos} onOpen={(idx) => setLightboxIdx(idx)} />
        )}
      </div>

      {lightboxPhoto && (
        <PhotoLightbox
          photo={lightboxPhoto}
          onClose={() => setLightboxIdx(null)}
          onPrev={hasPrev ? () => setLightboxIdx((i) => (i != null ? i - 1 : null)) : undefined}
          onNext={hasNext ? () => setLightboxIdx((i) => (i != null ? i + 1 : null)) : undefined}
          canDelete={canDeletePhoto(lightboxPhoto)}
          onDelete={() => setConfirmDelete(lightboxPhoto)}
          categories={categoriesQ.data ?? []}
          onMove={(newCategoryId) => {
            updatePhotoM.mutate(
              { id: lightboxPhoto.id, patch: { category_id: newCategoryId } },
              {
                onError: (err) =>
                  alert(apiErrorMessage(err, 'Verschieben fehlgeschlagen')),
              },
            );
          }}
        />
      )}

      <ConfirmDialog
        open={confirmDelete != null}
        title="Foto löschen?"
        danger
        message={
          confirmDelete?.caption
            ? `"${confirmDelete.caption}" wirklich löschen?`
            : 'Dieses Foto wirklich löschen?'
        }
        confirmLabel="Löschen"
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (!confirmDelete) return;
          deletePhotoM.mutate(confirmDelete.id, {
            onSuccess: () => {
              setConfirmDelete(null);
              setLightboxIdx(null);
            },
            onError: (err) => alert(apiErrorMessage(err, 'Löschen fehlgeschlagen')),
          });
        }}
      />

      <ConfirmDialog
        open={confirmDeleteCategory != null}
        title="Kategorie löschen?"
        message={
          confirmDeleteCategory
            ? `Leere Kategorie "${confirmDeleteCategory.name}" löschen?`
            : ''
        }
        confirmLabel="Löschen"
        onCancel={() => setConfirmDeleteCategory(null)}
        onConfirm={() => {
          if (!confirmDeleteCategory) return;
          deleteCategoryM.mutate(confirmDeleteCategory.id, {
            onSuccess: () => {
              setConfirmDeleteCategory(null);
              if (activeCategoryId === confirmDeleteCategory.id) {
                setActiveCategoryId('all');
              }
            },
            onError: (err) => alert(apiErrorMessage(err, 'Löschen fehlgeschlagen')),
          });
        }}
      />
    </div>
  );
}

function CategoryTab({
  active,
  onClick,
  onDelete,
  children,
}: {
  active: boolean;
  onClick: () => void;
  onDelete?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      <button
        type="button"
        onClick={onClick}
        style={{
          padding: '7px 12px',
          fontSize: 13,
          fontWeight: 500,
          color: active ? 'var(--color-primary)' : 'var(--color-text-soft)',
          background: active ? 'var(--color-surface-alt)' : 'transparent',
          border: '1px solid',
          borderColor: active ? 'var(--color-primary)' : 'transparent',
          borderRadius: 6,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        {children}
      </button>
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          title="Leere Kategorie löschen"
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--color-text-soft)',
            cursor: 'pointer',
            fontSize: 16,
            padding: '0 4px',
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}

function Grid({
  photos,
  allPhotos,
  onOpen,
}: {
  photos: Photo[];
  allPhotos: Photo[];
  onOpen: (indexInAll: number) => void;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
        gap: 10,
      }}
    >
      {photos.map((p) => {
        const idxInAll = allPhotos.indexOf(p);
        return (
          <div
            key={p.id}
            onClick={() => onOpen(idxInAll)}
            style={{ cursor: 'pointer', position: 'relative' }}
            title={
              p.caption ??
              `${p.original_name ?? 'Foto'} — ${dayjs(p.uploaded_at).format('DD.MM.YYYY HH:mm')}`
            }
          >
            <AuthedImage
              photoId={p.id}
              alt={p.caption ?? `Foto ${p.id}`}
              style={{
                aspectRatio: '4 / 3',
                borderRadius: 6,
                border: '1px solid var(--color-border)',
              }}
            />
            {p.caption && (
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--color-text-strong)',
                  marginTop: 4,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {p.caption}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
